/**
 * One tenant's override for which cost their AI assistant shows (0174).
 *
 * Four options, and the first is a real one: **Follow the platform setting** is
 * how an operator returns to inheritance after trying any of the others. A
 * control that could only move away from `null` would be a one-way door, and
 * the column is nullable precisely so it is not.
 *
 * ── Four options HERE, three on the platform editor ─────────────────────
 *
 * Not an inconsistency. On the deployment-wide screen `null` and 'hidden' are
 * the same state (unset resolves 'hidden'), so offering both would be two names
 * for one thing. Here `null` means something no other option does — FOLLOW the
 * deployment, and keep following it when it next changes — which is the only
 * way back after trying any of the others. The column is nullable precisely so
 * this is not a one-way door.
 *
 * ── It renders no "in force" value, deliberately ────────────────────────
 *
 * Its neighbours on this tab (the ceilings, the display currency) all show what
 * is resolved beside what is stored, because their chains end in one answer.
 * This one cannot: the result is CLAMPED per viewer — a platform admin sees
 * provider cost where everyone else sees the charged figure, from the identical
 * stored value — so any single figure printed here would be wrong for most of
 * the people who actually open that tenant's chat. The inherit option names what
 * it means instead of asserting what it resolves to.
 *
 * ── "What the platform pays" is safe to offer ───────────────────────────
 *
 * It cannot leak provider cost to the customer. `resolveChatCostVisibility`
 * downgrades it to customer cost for any viewer who is not a platform admin,
 * and `GET /api/agent/usage` never runs the provider-cost query for such a
 * caller — the clamp is server-side, not a UI decision. So this control only
 * ever changes what a PLATFORM ADMIN sees when they open that tenant's chat.
 * The copy says so, because an operator who does not know it will not use it.
 *
 * Saves on click rather than staging behind the page's Save bar. The staged-edit
 * rule exists for money VALUES, where a mistyped digit plus a stray click moves
 * a real limit; this is a four-way mode with its effect named on every row and
 * nothing to mistype. Same call `PlanAuthoritySetting` makes on the platform
 * settings screen for the identical shape.
 */
import { useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast } from '@papan/core-ui/ui'
import type { ChatCostVisibility } from '@papan/types'
import { budgetApi } from '../../client/budget'

export function ChatCostVisibilityPicker({
  tenantId,
  stored,
  disabled,
  onSaved,
}: {
  tenantId: string
  /** AS STORED. `null` = follows the platform setting. */
  stored: ChatCostVisibility | null
  disabled?: boolean
  onSaved: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const [saving, setSaving] = useState(false)

  const OPTIONS: Array<{ value: ChatCostVisibility | null; label: string; means: string }> = [
    {
      value: null,
      label: t('settings.platform.customer.chatCostInherit', { defaultValue: 'Follow the platform setting' }),
      means: t('settings.platform.customer.chatCostInheritMeans', {
        defaultValue: 'Whatever the deployment is configured to show. Changing that setting later moves this tenant with it.',
      }),
    },
    {
      value: 'user',
      label: t('settings.platform.settings.chatCostUser', { defaultValue: 'What the customer is charged' }),
      means: t('settings.platform.customer.chatCostUserMeans', {
        defaultValue: 'The figure this tenant’s spending ceilings are measured against.',
      }),
    },
    {
      value: 'operator',
      label: t('settings.platform.settings.chatCostOperator', { defaultValue: 'What the platform pays' }),
      means: t('settings.platform.customer.chatCostOperatorMeans', {
        defaultValue: 'Your provider cost, visible only to platform admins. This tenant’s own members still see what they are charged.',
      }),
    },
    {
      value: 'hidden',
      label: t('settings.platform.settings.chatCostHidden', { defaultValue: 'Hidden' }),
      means: t('settings.platform.customer.chatCostHiddenMeans', {
        defaultValue: 'No cost anywhere in their chat window. Their Spending screen is unaffected.',
      }),
    },
  ]
  const current = OPTIONS.find(o => o.value === stored) ?? OPTIONS[0]!

  const choose = async (value: ChatCostVisibility | null) => {
    if (value === stored) return
    setSaving(true)
    try {
      await budgetApi.setChatCostVisibility(tenantId, value)
      toast.success(t('settings.platform.customer.chatCostSaved', {
        defaultValue: 'Chat cost display updated. Their next load picks it up.',
      }))
      onSaved()
    } catch (e: any) {
      toast.error(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="themed-text-secondary text-[11px]">
        {t('settings.platform.customer.chatCostLabel', { defaultValue: 'This tenant’s chat shows' })}
      </span>
      <div className="flex flex-wrap gap-1" role="group">
        {OPTIONS.map(o => (
          <button
            key={o.value ?? 'inherit'}
            onClick={() => { void choose(o.value) }}
            disabled={saving || disabled}
            aria-pressed={o.value === stored}
            className={`px-2.5 py-1 text-xs rounded disabled:opacity-50 ${
              o.value === stored ? 'themed-btn-primary' : 'themed-btn-secondary'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      <span className="themed-text-muted text-[10px]">{current.means}</span>
    </div>
  )
}
